import firebase from 'firebase/app';

import { getFirebaseFunctionURL } from '../../../../utils/remote';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_BYTES } from './constants';
import {
  BREVO_AUTH_COOKIE_HEADER,
  getBrevoAuthCookie,
  throwIfBrevoSessionUnavailable,
} from './brevoAuth';

const UPLOAD_BREVO_IMAGE_PATH = 'leadCampaign-uploadBrevoImage';

export const ImageUploadErrorCode = {
  INVALID_TYPE: 'INVALID_TYPE',
  TOO_LARGE: 'TOO_LARGE',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
};

export class ImageUploadError extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

export const validateImage = (file) => {
  if (!file || !ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new ImageUploadError(ImageUploadErrorCode.INVALID_TYPE);
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new ImageUploadError(ImageUploadErrorCode.TOO_LARGE);
  }
};

/**
 * The stored shape — what Firestore holds and what an upload returns — carries fileSize as a
 * string; the UI shape carries sizeBytes as a number. These two convert between them.
 */
export const toStoredImage = (image) => ({
  url: image.url,
  fileName: image.fileName,
  fileSize: String(image.sizeBytes),
});

export const fromStoredImage = (stored) => (stored?.url ? {
  url: stored.url,
  fileName: stored.fileName || '',
  sizeBytes: Number(stored.fileSize) || 0,
} : null);

export const uploadCampaignImage = async ({ file }) => {
  validateImage(file);

  const idToken = await firebase.auth().currentUser.getIdToken();

  const form = new FormData();
  form.append('url', file, file.name);

  const response = await fetch(getFirebaseFunctionURL(UPLOAD_BREVO_IMAGE_PATH), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      [BREVO_AUTH_COOKIE_HEADER]: getBrevoAuthCookie(),
    },
    body: form,
  });

  throwIfBrevoSessionUnavailable(response);

  if (!response.ok) {
    throw new ImageUploadError(ImageUploadErrorCode.UPLOAD_FAILED);
  }

  const result = await response.json();
  if (!result?.url) {
    throw new ImageUploadError(ImageUploadErrorCode.UPLOAD_FAILED);
  }
  return {
    url: result.url,
    fileName: result.name,
    fileSize: String(result.size),
  };
};
